import { useRef, useState, useEffect } from "react";
import {
  Bot,
  Globe,
  TrendingDown,
  Award,
  Handshake,
  MessageSquare,
  Shield,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import "./LandingPage.css";

export function LandingPage() {
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [showFab, setShowFab] = useState(false);
  const [animationData, setAnimationData] = useState<any>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    workEmail: "",
    company: "",
    areaOfInterest: "",
    projectDescription: "",
  });
  const [formStatus, setFormStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [formError, setFormError] = useState("");

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.workEmail) return;

    setFormStatus("loading");
    setFormError("");

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL
          ? `${process.env.REACT_APP_API_URL}/contact`
          : "/api/contact",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: formData.firstName || undefined,
            lastName: formData.lastName || undefined,
            workEmail: formData.workEmail,
            company: formData.company || undefined,
            areaOfInterest: formData.areaOfInterest || undefined,
            projectDescription: formData.projectDescription || undefined,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to submit");
      setFormStatus("success");
    } catch (err) {
      setFormStatus("error");
      setFormError(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  useEffect(() => {
    fetch("/Group-1.json")
      .then((res) => res.json())
      .then((data) => setAnimationData(data));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFab(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    if (buttonRef.current) {
      observer.observe(buttonRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="landing-page-wrapper">
      <div className="lottie-background-2">
        {animationData && (
          <Lottie
            lottieRef={lottieRef}
            animationData={animationData}
            loop={true}
            onDOMLoaded={() => lottieRef.current?.setSpeed(0.3)}
          />
        )}
      </div>
      <div className="landing-page">
        <div className="landing-top">
          <div className="badge">
            <svg
              viewBox="0 0 88 88"
              width="14"
              height="14"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: "2px" }}
            >
              <path fill="#f35325" d="M0 0h42v42H0z" />
              <path fill="#81bc06" d="M46 0h42v42H46z" />
              <path fill="#05a6f0" d="M0 46h42v42H0z" />
              <path fill="#ffba08" d="M46 46h42v42H46z" />
            </svg>{" "}
            Microsoft Solutions Partner
          </div>

          <div
            className="landing-hero"
            style={{ alignItems: "center", gap: "2rem" }}
          >
            <div className="hero-content">
              <h1 className="title">
                High-performing agents require high-performing systems. We build
                the foundation for both.
              </h1>
              <p className="subtitle" style={{ marginBottom: "2rem" }}>
                We help companies prepare their systems, data, and processes so
                AI agents actually deliver real business value.
              </p>

              <div className="btn-group">
                <button
                  ref={buttonRef}
                  className="btn btn-primary attention-glow"
                  onClick={() => navigate("/explore")}
                >
                  Talk to our agent <Bot size={20} />
                </button>

                <a
                  className="btn btn-outline"
                  href="https://findmore.solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "none",
                    display: "inline-block",
                    textAlign: "center",
                  }}
                >
                  Reach out for more information
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="landing-bottom">
          <h2 className="section-title">Connecting Tech to Human Value</h2>
          <p style={{ textAlign: "center", marginBottom: "2rem" }}>
            From strategic consulting to full-stack development, with
            specialized teams for each technology
          </p>

          <div className="tech-stack-grid">
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="Dynamics">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="61.0904 5.5368 355.9882 488.2116"
                    width="24"
                    height="24"
                    preserveAspectRatio="none"
                  >
                    <g
                      clip-path="url(#clip0)"
                      transform="matrix(5.085537910461426, 0, 0, 5.085537910461426, 0.06396700537156619, 5.53684815074277)"
                      id="object-0"
                    >
                      <defs>
                        <filter id="filter0_f"></filter>
                        <filter id="filter1_f"></filter>
                        <linearGradient
                          id="paint0_linear"
                          x1="38.0451"
                          y1="-1"
                          x2="56.6585"
                          y2="47.7233"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#0B53CE"></stop>
                          <stop offset="1" stop-color="#7252AA"></stop>
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear"
                          x1="64.1377"
                          y1="93.4922"
                          x2="64.1377"
                          y2="35.4151"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#2266E3"></stop>
                          <stop offset="1" stop-color="#AE7FE2"></stop>
                        </linearGradient>
                        <linearGradient
                          id="paint2_linear"
                          x1="82"
                          y1="56.7858"
                          x2="62.0764"
                          y2="56.7858"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#94B9FF"></stop>
                          <stop
                            offset="0.2878"
                            stop-color="#94B9FF"
                            stop-opacity="0.523646"
                          ></stop>
                          <stop
                            offset="1"
                            stop-color="#538FFF"
                            stop-opacity="0"
                          ></stop>
                        </linearGradient>
                        <clipPath id="clip0">
                          <rect width="96" height="96" fill="white"></rect>
                        </clipPath>
                      </defs>
                      <mask
                        id="mask0"
                        maskUnits="userSpaceOnUse"
                        x="12"
                        y="0"
                        width="70"
                        height="96"
                      >
                        <path
                          d="M82.0001 31.047C82.0001 26.8209 79.3434 23.051 75.3634 21.6296L17.3453 0.90903C14.7404 -0.0213096 12 1.90988 12 4.676V36.1811C12 37.8715 13.0627 39.3795 14.6547 39.9481L40.6547 49.2338C43.2596 50.1641 46 48.2329 46 45.4668V27.3768C46 25.9794 47.3966 25.0127 48.7044 25.5049L55.5222 28.0707C59.4195 29.5374 62 33.2657 62 37.4299V45.3076L32.6272 56.0399C31.0495 56.6164 30 58.1172 30 59.797V91.2797C30 94.0582 32.7631 95.9903 35.3728 95.0367L75.432 80.3996C79.3763 78.9584 82 75.2064 82 71.007L82.0001 31.047Z"
                          fill="white"
                        ></path>
                      </mask>
                      <g mask="url(#mask0)">
                        <path
                          d="M12 -1L82.0001 23.9998V58.3245C82.0001 61.0905 79.2601 63.0217 76.6551 62.0917L62 56.8593V37.4241C62 33.258 59.4171 29.5283 55.5172 28.063L48.7034 25.5029C47.3957 25.0116 46 25.9782 46 27.3751V51.1428L12 39V-1Z"
                          fill="url(#paint0_linear)"
                        ></path>
                        <g filter="url(#filter0_f)">
                          <path
                            d="M82 31.3998V31.3998C82 35.5992 79.3763 39.3586 75.4319 40.7998L30 57.3999V97.3999L82 78.3998V31.3998Z"
                            fill="black"
                            fill-opacity="0.24"
                          ></path>
                        </g>
                        <g filter="url(#filter1_f)">
                          <path
                            d="M82 32.9998V32.9998C82 37.1992 79.3763 40.9585 75.4319 42.3997L30 58.9999V98.9999L82 79.9998V32.9998Z"
                            fill="black"
                            fill-opacity="0.32"
                          ></path>
                        </g>
                        <path
                          d="M82 30.9998V30.9998C82 35.1992 79.3763 38.9585 75.4319 40.3997L30 56.9999V96.9999L82 77.9998V30.9998Z"
                          fill="url(#paint1_linear)"
                        ></path>
                        <path
                          opacity="0.5"
                          d="M82 30.9998V30.9998C82 35.1992 79.3763 38.9585 75.4319 40.3997L30 56.9999V96.9999L82 77.9998V30.9998Z"
                          fill="url(#paint2_linear)"
                        ></path>
                        <path
                          opacity="0.5"
                          d="M62.0013 45.3202L45.9922 51.1768L45.9923 74.6349C45.9923 76.0323 47.389 76.9989 48.6968 76.5066L55.5241 73.9368C59.4211 72.4699 62.0013 68.7418 62.0013 64.5779V45.3202Z"
                          fill="#B0ADFF"
                        ></path>
                      </g>
                    </g>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">Dynamics 365</h3>
              <p className="feature-desc">
                Equip your human agents with a 360-degree view of the customer
                to drive meaningful engagement.
              </p>
            </div>
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="Power Platform">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 96 96"
                    fill="none"
                  >
                    <defs>
                      <filter id="filter0_f_84_430"></filter>
                      <filter id="filter1_f_84_430"></filter>
                      <linearGradient
                        id="paint0_linear_84_430"
                        x1="9"
                        y1="58"
                        x2="19"
                        y2="96"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#159455"></stop>
                        <stop offset="1" stop-color="#3FBDA9"></stop>
                      </linearGradient>
                      <linearGradient
                        id="paint1_linear_84_430"
                        x1="24.8987"
                        y1="5.52273"
                        x2="82.9153"
                        y2="31.3167"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#23A794"></stop>
                        <stop offset="0.568447" stop-color="#007A84"></stop>
                        <stop offset="1" stop-color="#005158"></stop>
                      </linearGradient>
                      <linearGradient
                        id="paint2_linear_84_430"
                        x1="74"
                        y1="40"
                        x2="63.5"
                        y2="12.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#004A8B"></stop>
                        <stop
                          offset="0.405563"
                          stop-color="#105DA8"
                          stop-opacity="0.500243"
                        ></stop>
                        <stop
                          offset="1"
                          stop-color="#2170C6"
                          stop-opacity="0"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        id="paint3_linear_84_430"
                        x1="17"
                        y1="36"
                        x2="69.5"
                        y2="62"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#7FD9A2"></stop>
                        <stop offset="0.196063" stop-color="#47BF79"></stop>
                        <stop offset="0.713946" stop-color="#009280"></stop>
                        <stop offset="1" stop-color="#007A84"></stop>
                      </linearGradient>
                      <linearGradient
                        id="paint4_linear_84_430"
                        x1="16.5"
                        y1="37"
                        x2="28.5"
                        y2="43"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#A8E47C" stop-opacity="0.86"></stop>
                        <stop
                          offset="0.367474"
                          stop-color="#87D152"
                          stop-opacity="0.2"
                        ></stop>
                        <stop
                          offset="1"
                          stop-color="#58BE5A"
                          stop-opacity="0"
                        ></stop>
                      </linearGradient>
                    </defs>
                    <mask
                      id="mask0_84_430"
                      maskUnits="userSpaceOnUse"
                      x="3"
                      y="2"
                      width="88"
                      height="92"
                    >
                      <path
                        d="M36.2108 2H75.7297C86.7897 2 94.0145 13.5445 89.2654 23.4644L90.0002 22L76.2654 49.4696C76.2262 49.5513 76.1862 49.6329 76.1455 49.7144L74.0002 54L76.1461 49.7082C73.6052 54.79 68.4112 58 62.7297 58H39.0002L22.1995 91.6014C20.8813 94.2378 17.1191 94.2378 15.8009 91.6014L4.06542 68.1305C2.99573 65.9911 3.13282 63.6781 4.09866 61.803L17.0229 35.9545C18.2347 33.5309 20.7118 32 23.4215 32H72.9425C70.3205 29.5491 66.7837 28 62.7321 28H26.7877C24.1287 28 22.3993 25.2017 23.5884 22.8234L33.0115 3.97728C33.6174 2.76547 34.8559 2 36.2108 2Z"
                        fill="white"
                      ></path>
                    </mask>
                    <g mask="url(#mask0_84_430)">
                      <path
                        d="M10.3262 58H39L22.1993 91.6014C20.8811 94.2378 17.1189 94.2378 15.8007 91.6014L4.06525 68.1305C1.73809 63.4762 5.12256 58 10.3262 58Z"
                        fill="url(#paint0_linear_84_430)"
                      ></path>
                      <path
                        d="M36.2106 2H75.7295C86.8802 2 94.1327 13.7347 89.1459 23.7082L76.2652 49.4696C76.226 49.5513 76.1861 49.6329 76.1453 49.7144L74 54L76.2652 49.4696C81.0192 39.5494 73.7946 28 62.732 28H26.7876C24.1286 28 22.3991 25.2017 23.5883 22.8234L33.0113 3.97728C33.6172 2.76547 34.8557 2 36.2106 2Z"
                        fill="url(#paint1_linear_84_430)"
                      ></path>
                      <path
                        d="M36.2106 2H75.7295C86.8802 2 94.1327 13.7347 89.1459 23.7082L76.2366 49.5268C76.2062 49.5896 76.1753 49.6523 76.144 49.7149L75 52L76.2366 49.5268C81.0459 39.5939 73.8149 28 62.7309 28H26.7876C24.1286 28 22.3991 25.2017 23.5883 22.8234L33.0113 3.97728C33.6172 2.76547 34.8557 2 36.2106 2Z"
                        fill="url(#paint2_linear_84_430)"
                      ></path>
                      <g filter="url(#filter0_f_84_430)">
                        <path
                          d="M62.7295 58.4H10.4213C7.71219 58.4 5.23554 59.9303 4.02347 62.3531L17.0227 36.3546C18.2345 33.931 20.7116 32.4 23.4213 32.4H75.7264C81.4004 32.4 86.5887 29.1986 89.1333 24.1273L90 22.4L76.1459 50.1082C73.605 55.19 68.4111 58.4 62.7295 58.4Z"
                          fill="black"
                          fill-opacity="0.24"
                        ></path>
                      </g>
                      <g filter="url(#filter1_f_84_430)">
                        <path
                          d="M62.7295 60H10.4213C7.71219 60 5.23554 61.5303 4.02347 63.9531L17.0227 37.9545C18.2345 35.5309 20.7116 34 23.4213 34H75.7264C81.4004 34 86.5887 30.7985 89.1333 25.7272L90 24L76.1459 51.7082C73.605 56.79 68.4111 60 62.7295 60Z"
                          fill="black"
                          fill-opacity="0.32"
                        ></path>
                      </g>
                      <path
                        d="M62.7295 58H10.4213C7.71219 58 5.23554 59.5303 4.02347 61.9531L17.0227 35.9545C18.2345 33.5309 20.7116 32 23.4213 32H75.7264C81.4004 32 86.5887 28.7985 89.1333 23.7272L90 22L76.1459 49.7082C73.605 54.79 68.4111 58 62.7295 58Z"
                        fill="url(#paint3_linear_84_430)"
                      ></path>
                      <path
                        opacity="0.7"
                        d="M62.7004 58H10.4598C7.75011 58 5.27299 59.5309 4.06119 61.9546L17.0612 35.9545C18.273 33.5309 20.7501 32 23.4598 32H76C81.4957 32 86.5234 28.9061 89 24L76.1107 49.7203C73.5675 54.7954 68.3771 58 62.7004 58Z"
                        fill="url(#paint4_linear_84_430)"
                      ></path>
                    </g>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">Power Platform</h3>
              <p className="feature-desc">
                Free your specialists from manual tasks, allowing them to act as
                strategic agents of growth.
              </p>
            </div>
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="Copilot Studio">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M34.142 7.325A4.63 4.63 0 0029.7 4H28.35a4.63 4.63 0 00-4.554 3.794L21.48 20.407l.575-1.965a4.63 4.63 0 014.444-3.33h7.853l3.294 1.282 3.175-1.283h-.926a4.63 4.63 0 01-4.443-3.325l-1.31-4.461z"
                      fill="url(#prefix__paint0_radial_56201_15503)"
                    />
                    <path
                      d="M14.33 40.656A4.63 4.63 0 0018.779 44h2.87a4.63 4.63 0 004.629-4.51l.312-12.163-.654 2.233a4.63 4.63 0 01-4.443 3.329h-7.919l-2.823-1.532-3.057 1.532h.912a4.63 4.63 0 014.447 3.344l1.279 4.423z"
                      fill="url(#prefix__paint1_radial_56201_15503)"
                    />
                    <path
                      d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z"
                      fill="url(#prefix__paint2_linear_56201_15503)"
                    />
                    <path
                      d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z"
                      fill="url(#prefix__paint3_linear_56201_15503)"
                    />
                    <path
                      d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z"
                      fill="url(#prefix__paint4_radial_56201_15503)"
                    />
                    <path
                      d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z"
                      fill="url(#prefix__paint5_linear_56201_15503)"
                    />
                    <defs>
                      <radialGradient
                        id="prefix__paint0_radial_56201_15503"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="matrix(-10.96051 -13.38922 12.59013 -10.30637 38.005 20.514)"
                      >
                        <stop offset=".096" stop-color="#00AEFF" />
                        <stop offset=".773" stop-color="#2253CE" />
                        <stop offset="1" stop-color="#0736C4" />
                      </radialGradient>
                      <radialGradient
                        id="prefix__paint1_radial_56201_15503"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="rotate(51.84 -28.201 27.85) scale(15.9912 15.5119)"
                      >
                        <stop stop-color="#FFB657" />
                        <stop offset=".634" stop-color="#FF5F3D" />
                        <stop offset=".923" stop-color="#C02B3C" />
                      </radialGradient>
                      <radialGradient
                        id="prefix__paint4_radial_56201_15503"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="rotate(109.274 16.301 20.802) scale(38.3873 45.9867)"
                      >
                        <stop offset=".066" stop-color="#8C48FF" />
                        <stop offset=".5" stop-color="#F2598A" />
                        <stop offset=".896" stop-color="#FFB152" />
                      </radialGradient>
                      <linearGradient
                        id="prefix__paint2_linear_56201_15503"
                        x1="12.5"
                        y1="7.5"
                        x2="14.788"
                        y2="33.975"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset=".156" stop-color="#0D91E1" />
                        <stop offset=".487" stop-color="#52B471" />
                        <stop offset=".652" stop-color="#98BD42" />
                        <stop offset=".937" stop-color="#FFC800" />
                      </linearGradient>
                      <linearGradient
                        id="prefix__paint3_linear_56201_15503"
                        x1="14.5"
                        y1="4"
                        x2="15.75"
                        y2="32.885"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#3DCBFF" />
                        <stop
                          offset=".247"
                          stop-color="#0588F7"
                          stop-opacity="0"
                        />
                      </linearGradient>
                      <linearGradient
                        id="prefix__paint5_linear_56201_15503"
                        x1="42.586"
                        y1="13.346"
                        x2="42.569"
                        y2="21.215"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset=".058" stop-color="#F8ADFA" />
                        <stop
                          offset=".708"
                          stop-color="#A86EDD"
                          stop-opacity="0"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">Copilot Studio</h3>
              <p className="feature-desc">
                An intelligent partner that augments your team’s expertise with
                real-time data and insights.
              </p>
            </div>

            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="Full Stack">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="uuid-f5106e48-b389-4501-93ec-6a1c544d2838"
                    width="24"
                    height="24"
                    viewBox="0 0 18 18"
                  >
                    <defs>
                      <linearGradient
                        id="uuid-cc58cd68-2d17-426f-9a1d-e639f5f1a0dd"
                        x1="7.057"
                        y1="781.481"
                        x2="7.057"
                        y2="791.514"
                        gradientTransform="translate(0 791.516) scale(1 -1)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0" stop-color="#32bedd"></stop>
                        <stop offset="1" stop-color="#50e6ff"></stop>
                      </linearGradient>
                      <linearGradient
                        id="uuid-6861d0e2-dc4a-411a-a07e-aefc3d3a430b"
                        x1="11.43"
                        y1="776.984"
                        x2="11.43"
                        y2="785.557"
                        gradientTransform="translate(0 791.516) scale(1 -1)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0" stop-color="#0078d4"></stop>
                        <stop offset=".82" stop-color="#5ea0ef"></stop>
                      </linearGradient>
                      <linearGradient
                        id="uuid-4b30bbba-d707-4e16-8f0f-12ca027b18b4"
                        x1="11.43"
                        y1="773.676"
                        x2="11.43"
                        y2="776.984"
                        gradientTransform="translate(0 791.516) scale(1 -1)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset=".15" stop-color="#ccc"></stop>
                        <stop offset="1" stop-color="#707070"></stop>
                      </linearGradient>
                      <linearGradient
                        id="uuid-3e5d9740-952e-41fc-9a07-33e4ad62f0e5"
                        x1="7.994"
                        y1="780.47"
                        x2="10.144"
                        y2="780.47"
                        gradientTransform="translate(0 791.516) scale(1 -1)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0" stop-color="#5ea0ef"></stop>
                        <stop offset=".372" stop-color="#9fc6f5"></stop>
                        <stop offset=".8" stop-color="#e4effc"></stop>
                        <stop offset="1" stop-color="#fff"></stop>
                      </linearGradient>
                      <linearGradient
                        id="uuid-729abf50-f13a-4d76-a41d-4177b7156027"
                        x1="12.719"
                        y1="780.47"
                        x2="14.869"
                        y2="780.47"
                        gradientTransform="translate(0 791.516) scale(1 -1)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0" stop-color="#fff"></stop>
                        <stop offset=".2" stop-color="#e4effc"></stop>
                        <stop offset=".628" stop-color="#9fc6f5"></stop>
                        <stop offset="1" stop-color="#5ea0ef"></stop>
                      </linearGradient>
                    </defs>
                    <path
                      d="M14.113,6.871c-.021-.752-.309-1.472-.813-2.03-.504-.558-1.191-.919-1.936-1.017-.045-1.051-.503-2.041-1.273-2.757C9.32,.352,8.298-.031,7.247,.002c-.849-.016-1.682,.231-2.384,.708-.702,.477-1.239,1.161-1.536,1.956-.898,.109-1.728,.536-2.338,1.204C.378,4.538,.028,5.403,0,6.307c.039,1.016,.479,1.975,1.224,2.666,.745,.692,1.734,1.06,2.749,1.024l.351-.014h6.439c.058,0,.115-.01,.17-.026,.825-.006,1.616-.331,2.208-.905,.592-.575,.94-1.355,.972-2.18h0Z"
                      fill="url(#uuid-cc58cd68-2d17-426f-9a1d-e639f5f1a0dd)"
                    ></path>
                    <path
                      d="M17.431,5.959H5.429c-.237,0-.429,.192-.429,.429v7.716c0,.237,.192,.429,.429,.429h12.003c.237,0,.429-.192,.429-.429V6.387c0-.237-.192-.429-.429-.429Z"
                      fill="url(#uuid-6861d0e2-dc4a-411a-a07e-aefc3d3a430b)"
                    ></path>
                    <path
                      d="M14.009,17.125c-1.272-.2-1.322-1.115-1.322-2.593h-2.522c0,1.479-.043,2.393-1.315,2.593-.176,.02-.338,.103-.455,.235-.117,.132-.182,.303-.181,.479h6.43c.001-.177-.063-.347-.181-.479-.118-.132-.28-.216-.455-.235Z"
                      fill="url(#uuid-4b30bbba-d707-4e16-8f0f-12ca027b18b4)"
                    ></path>
                    <g>
                      <path
                        d="M8.056,10.157l.232-.231,1.824,1.829c.02,.02,.031,.046,.031,.074,0,.028-.011,.054-.031,.074l-.232,.231c-.02,.02-.046,.031-.074,.031-.028,0-.054-.011-.074-.031l-1.676-1.681c-.039-.039-.061-.093-.061-.148,0-.056,.022-.109,.062-.148h0Z"
                        fill="url(#uuid-3e5d9740-952e-41fc-9a07-33e4ad62f0e5)"
                      ></path>
                      <path
                        d="M8.284,10.685l-.231-.232c-.039-.039-.061-.093-.061-.148s.022-.109,.062-.148l1.71-1.705c.02-.02,.046-.031,.074-.031,.028,0,.054,.011,.074,.031l.231,.232c.02,.02,.031,.046,.031,.074s-.011,.054-.031,.074l-1.859,1.853Z"
                        fill="#f2f2f2"
                      ></path>
                      <path
                        d="M12.981,12.136l-.232-.231c-.01-.01-.017-.021-.023-.034-.005-.013-.008-.026-.008-.04s.003-.027,.008-.04c.005-.013,.013-.024,.023-.034l1.826-1.831,.232,.231c.039,.039,.061,.093,.062,.148s-.022,.109-.061,.148l-1.678,1.683c-.02,.02-.046,.031-.074,.031-.028,0-.054-.011-.074-.031h0Z"
                        fill="url(#uuid-729abf50-f13a-4d76-a41d-4177b7156027)"
                      ></path>
                      <path
                        d="M12.714,8.685l.231-.232c.02-.02,.046-.031,.074-.031,.028,0,.054,.011,.074,.031l1.71,1.705c.039,.039,.062,.093,.062,.148,0,.056-.022,.109-.061,.148l-.231,.232-1.857-1.852c-.01-.01-.018-.021-.024-.034-.006-.013-.009-.027-.009-.04,0-.014,.002-.028,.008-.041,.005-.013,.013-.025,.023-.035Z"
                        fill="#f2f2f2"
                      ></path>
                      <path
                        d="M12.425,7.877l-.37-.118c-.039-.013-.081,.009-.094,.048l-1.509,4.712c-.013,.039,.009,.082,.048,.094l.37,.118c.039,.013,.082-.009,.094-.049l1.509-4.712c.013-.039-.009-.081-.048-.094Z"
                        fill="#f2f2f2"
                      ></path>
                    </g>
                    <rect y="0" width="18" height="18" fill="none"></rect>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">Full Stack</h3>
              <p className="feature-desc">
                Provide your development agents with a versatile, end-to-end
                technical stack to build seamless and high-performing digital
                experiences.
              </p>
            </div>
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="HubSpot">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    shape-rendering="geometricPrecision"
                    text-rendering="geometricPrecision"
                    image-rendering="optimizeQuality"
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    viewBox="0 0 489 511.8"
                  >
                    <path
                      fill="#FF7A59"
                      fill-rule="nonzero"
                      d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z"
                    ></path>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">HubSpot (Elite Partner)</h3>
              <p className="feature-desc">
                Enable your growth agents with streamlined CRM and Revenue
                Operations to maximize every stage of the customer journey.
              </p>
            </div>
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="Cloud & DevOps">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="uuid-54cc9e20-2e30-4e65-a9a4-8bc4f07d47c6"
                    width="24"
                    height="24"
                    viewBox="0 0 18 18"
                  >
                    <g>
                      <rect
                        x="5.974"
                        y="0"
                        width="6.053"
                        height="6.053"
                        rx=".401"
                        ry=".401"
                        fill="#a67af4"
                      ></rect>
                      <path
                        d="M11.392,5.523H6.611c-.059,0-.107-.048-.107-.108V.638c0-.059,.048-.107,.107-.108h4.778c.059,0,.108,.048,.108,.108V5.415c0,.058-.046,.106-.105,.108Z"
                        fill="#773adc"
                      ></path>
                      <g>
                        <rect
                          x="7.202"
                          y=".983"
                          width="1.257"
                          height="4.087"
                          fill="#b796f9"
                        ></rect>
                        <rect
                          x="9.541"
                          y=".982"
                          width="1.257"
                          height="4.088"
                          fill="#b796f9"
                        ></rect>
                      </g>
                    </g>
                    <g>
                      <path
                        d="M8.045,10.794c.292-.107,.598-.161,.909-.161,.303,0,.602,.052,.888,.154l2.184,.779V7.225c0-.221-.179-.401-.401-.401H6.375c-.221,0-.401,.179-.401,.401v4.327l2.071-.758Z"
                        fill="#a67af4"
                      ></path>
                      <path
                        d="M8.045,10.794c.292-.107,.598-.161,.909-.161,.303,0,.602,.052,.888,.154l1.654,.59v-3.914c0-.059-.048-.108-.108-.108H6.611c-.059,0-.107,.048-.107,.108v3.896l1.541-.564Z"
                        fill="#773adc"
                      ></path>
                      <g>
                        <path
                          d="M9.842,10.786l.956,.341v-3.321h-1.257v2.894c.102,.023,.202,.05,.301,.085Z"
                          fill="#b796f9"
                        ></path>
                        <path
                          d="M8.045,10.794c.135-.05,.274-.087,.414-.113v-2.873h-1.257v3.295l.843-.309Z"
                          fill="#b796f9"
                        ></path>
                      </g>
                    </g>
                    <g>
                      <path
                        d="M5.264,2.406H1.159c-.204,0-.37,.166-.37,.37V7.623c0,.204,.166,.37,.37,.37H5.264V2.406Z"
                        fill="#a67af4"
                      ></path>
                      <path
                        d="M5.264,2.895H1.376c-.055,0-.099,.045-.099,.099V7.404c0,.055,.044,.099,.099,.099h3.887V2.895Z"
                        fill="#773adc"
                      ></path>
                      <g>
                        <rect
                          x="1.922"
                          y="3.313"
                          width="1.161"
                          height="3.773"
                          fill="#b796f9"
                        ></rect>
                        <rect
                          x="4.103"
                          y="3.312"
                          width="1.161"
                          height="3.774"
                          fill="#b796f9"
                        ></rect>
                      </g>
                    </g>
                    <g>
                      <path
                        d="M2.807,11.517c.237-.087,.485-.131,.737-.131,.246,0,.489,.042,.72,.125l.999,.356v-3.183H1.159c-.204,0-.37,.166-.37,.37v3.202l2.019-.739Z"
                        fill="#a67af4"
                      ></path>
                      <path
                        d="M2.807,11.517c.237-.087,.485-.131,.737-.131,.246,0,.489,.042,.72,.125l.999,.356v-2.694H1.376c-.055,0-.099,.045-.099,.099v2.804l1.53-.56Z"
                        fill="#773adc"
                      ></path>
                      <g>
                        <path
                          d="M4.287,11.511l.977,.349v-2.269h-1.161v1.864c.062,.016,.123,.035,.184,.056Z"
                          fill="#b796f9"
                        ></path>
                        <path
                          d="M2.807,11.517c.09-.033,.182-.058,.276-.078v-1.847H1.922v2.25l.885-.324Z"
                          fill="#b796f9"
                        ></path>
                      </g>
                    </g>
                    <g>
                      <path
                        d="M15.136,11.517c-.237-.087-.485-.131-.737-.131-.246,0-.489,.042-.72,.125l-.999,.356v-3.183h4.105c.204,0,.37,.166,.37,.37v3.202l-2.019-.739Z"
                        fill="#a67af4"
                      ></path>
                      <path
                        d="M15.136,11.517c-.237-.087-.485-.131-.737-.131-.246,0-.489,.042-.72,.125l-.999,.356v-2.694h3.887c.055,0,.099,.045,.099,.099v2.804l-1.53-.56Z"
                        fill="#773adc"
                      ></path>
                      <g>
                        <path
                          d="M13.656,11.511l-.977,.349v-2.269h1.161v1.864c-.062,.016-.123,.035-.184,.056Z"
                          fill="#b796f9"
                        ></path>
                        <path
                          d="M15.136,11.517c-.09-.033-.182-.058-.276-.078v-1.847h1.161v2.25l-.885-.324Z"
                          fill="#b796f9"
                        ></path>
                      </g>
                    </g>
                    <g>
                      <path
                        d="M12.68,2.406h4.105c.204,0,.37,.166,.37,.37V7.623c0,.204-.166,.37-.37,.37h-4.105V2.406Z"
                        fill="#a67af4"
                      ></path>
                      <path
                        d="M12.68,2.895h3.887c.055,0,.099,.045,.099,.099V7.404c0,.055-.044,.099-.099,.099h-3.887V2.895Z"
                        fill="#773adc"
                      ></path>
                      <g>
                        <rect
                          x="14.86"
                          y="3.313"
                          width="1.161"
                          height="3.773"
                          fill="#b796f9"
                        ></rect>
                        <rect
                          x="12.68"
                          y="3.312"
                          width="1.161"
                          height="3.774"
                          fill="#b796f9"
                        ></rect>
                      </g>
                    </g>
                    <path
                      d="M17.988,17.993H0l.004-1.012c.079,.007,.164,.011,.254,.011,.726-.001,1.09-.234,1.454-.467,.365-.233,.73-.467,1.459-.467h.003c.73,0,1.094,.233,1.459,.467,.365,.233,.729,.467,1.458,.467,.729,0,1.093-.234,1.458-.467,.365-.233,.73-.467,1.459-.467h.003c.182,0,.34,.015,.482,.04,.432,.077,.708,.252,.983,.428,.365,.233,.729,.465,1.456,.466,.728,0,1.092-.234,1.456-.467,.365-.233,.73-.467,1.459-.467h.014c.725,.004,1.09,.236,1.454,.468,.365,.233,.73,.466,1.459,.466,.075,0,.145-.002,.213-.007v1.008Z"
                      fill="#83b9f9"
                    ></path>
                    <g>
                      <path
                        d="M.092,13.594l.82,1.762,.026,.056h0l.22,.496,.128,.293c.051-.031,.102-.062,.156-.096,.38-.243,.853-.546,1.731-.546s1.349,.303,1.729,.546c.179,.114,.34,.214,.536,.284l-.243-.504-.014-.029-.032-.068-1.011-2.172-.013-.027-.01-.029c-.007-.019-.068-.194-.108-.366-.041-.176-.072-.383,.013-.586,.088-.212,.247-.385,.448-.492l-.372-.133c-.361-.129-.757-.127-1.117,.005l-2.221,.813H.758l-.58,.213c-.078,.029-.141,.089-.173,.166-.012,.03-.002,.098,.015,.171,.027,.116,.072,.244,.072,.244Z"
                        fill="#0078d4"
                      ></path>
                      <path
                        d="M4.698,13.354l1.011,2.172,.032,.068h0l.413,.893c.534-.014,.801-.175,1.125-.383,.38-.243,.853-.546,1.731-.546,.204,0,.391,.016,.57,.048,.531,.094,.868,.309,1.165,.499,.296,.189,.55,.336,.994,.372l.44-.873,.01,.003,.04-.081,.054-.109,.045-.091,.966-1.969s.154-.415,.114-.512c-.039-.097-.118-.172-.217-.208l-.818-.292h0l-2.74-.978c-.446-.159-.933-.157-1.377,.006l-2.738,1.003h0l-.715,.262c-.096,.035-.174,.109-.213,.204-.015,.036-.003,.121,.018,.21,.033,.143,.089,.301,.089,.301Z"
                        fill="#0078d4"
                      ></path>
                      <path
                        d="M13.967,12.607c.084,.203,.054,.411,.013,.586-.04,.171-.101,.346-.108,.366l-.01,.029-.013,.027-1.011,2.172-.032,.068-.014,.029-.249,.515c.213-.071,.385-.174,.575-.295,.372-.238,.833-.533,1.708-.544h.037c.872,.003,1.342,.304,1.721,.545,.043,.027,.083,.051,.125,.077l.12-.274,.22-.496h0l.026-.055,.82-1.762s.045-.128,.072-.244c.017-.073,.027-.141,.015-.171-.032-.077-.095-.137-.173-.166l-.58-.212h0l-2.221-.814c-.36-.132-.756-.134-1.117-.005l-.372,.133c.2,.107,.36,.28,.448,.492Z"
                        fill="#0078d4"
                      ></path>
                    </g>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">Cloud & DevOps</h3>
              <p className="feature-desc">
                Build a resilient environment where your technical agents can
                deploy, scale, and innovate with absolute security and
                confidence.
              </p>
            </div>
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="QA & Testing">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 18 18"
                  >
                    <defs>
                      <linearGradient
                        id="bc9022b1-0342-40a9-9cf5-001cbbd7832d"
                        x1="8.916"
                        y1="17.5"
                        x2="8.916"
                        y2="2.605"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0" stop-color="#773adc"></stop>
                        <stop offset="1" stop-color="#a67af4"></stop>
                      </linearGradient>
                    </defs>
                    <title>MsPortalFx.base.images-75</title>
                    <g id="bd1c5136-8a78-4293-a9c5-1141130b41f4">
                      <g>
                        <path
                          d="M9,4.759A.432.432,0,0,1,8.665,4.6l-2.8-3.4A.43.43,0,0,1,6.53.657L8.995,3.65,11.422.659a.43.43,0,0,1,.668.542L9.331,4.6A.431.431,0,0,1,9,4.759Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M1.621,8.837a.43.43,0,0,1-.43-.43V7.127a.43.43,0,0,1,.247-.389L5.957,4.619a.43.43,0,0,1,.365.778l-4.271,2V8.407A.431.431,0,0,1,1.621,8.837Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M1.621,12.741a.429.429,0,0,1-.43-.43V11.032a.428.428,0,0,1,.247-.389l4.519-2.12a.43.43,0,0,1,.365.779l-4.271,2v1.006A.43.43,0,0,1,1.621,12.741Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M1.621,16.845a.43.43,0,0,1-.43-.43v-1.28a.43.43,0,0,1,.247-.389l4.519-2.119a.43.43,0,0,1,.365.778l-4.271,2v1.007A.431.431,0,0,1,1.621,16.845Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M16.379,8.837a.431.431,0,0,1-.43-.43V7.4l-4.271-2a.43.43,0,0,1,.365-.778l4.519,2.119a.43.43,0,0,1,.247.389v1.28A.43.43,0,0,1,16.379,8.837Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M16.379,12.741a.43.43,0,0,1-.43-.43V11.305l-4.271-2a.43.43,0,0,1,.365-.779l4.519,2.12a.428.428,0,0,1,.247.389v1.279A.429.429,0,0,1,16.379,12.741Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M16.379,16.845a.431.431,0,0,1-.43-.43V15.408l-4.271-2a.43.43,0,0,1,.365-.778l4.519,2.119a.43.43,0,0,1,.247.389v1.28A.43.43,0,0,1,16.379,16.845Z"
                          fill="#773adc"
                        ></path>
                        <path
                          d="M13.345,7.034a4.429,4.429,0,1,0-8.857,0v6.038a4.429,4.429,0,0,0,8.857,0Z"
                          fill="url(#bc9022b1-0342-40a9-9cf5-001cbbd7832d)"
                        ></path>
                        <path
                          d="M12.817,4.937a4.44,4.44,0,0,0-1.8-1.8,1.29,1.29,0,0,0,1.8,1.8Z"
                          fill="#b796f9"
                        ></path>
                        <path
                          d="M5.016,4.937a1.29,1.29,0,0,0,1.8-1.8A4.44,4.44,0,0,0,5.016,4.937Z"
                          fill="#b796f9"
                        ></path>
                      </g>
                    </g>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">QA & Testing</h3>
              <p className="feature-desc">
                Ensure every process is bulletproof, protecting your business
                agents from friction and guaranteeing excellence in every
                release.
              </p>
            </div>
            <div className="card">
              <div className="icon-wrapper">
                <span role="img" aria-label="UX/UI Design">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 18 18"
                  >
                    <defs>
                      <linearGradient
                        id="a9c62307-1cd0-400c-911b-17ec6a9110ce"
                        x1="9"
                        y1="15.834"
                        x2="9"
                        y2="5.788"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0" stop-color="#32bedd"></stop>
                        <stop offset="0.775" stop-color="#32d4f5"></stop>
                      </linearGradient>
                    </defs>
                    <title>MsPortalFx.base.images-7</title>
                    <g id="f31d214e-f09e-49e3-b3d2-7c5d55682d09">
                      <g>
                        <path
                          d="M.5,5.788h17a0,0,0,0,1,0,0v9.478a.568.568,0,0,1-.568.568H1.068A.568.568,0,0,1,.5,15.266V5.788A0,0,0,0,1,.5,5.788Z"
                          fill="url(#a9c62307-1cd0-400c-911b-17ec6a9110ce)"
                        ></path>
                        <path
                          d="M1.071,2.166H16.929a.568.568,0,0,1,.568.568V5.788a0,0,0,0,1,0,0H.5a0,0,0,0,1,0,0V2.734A.568.568,0,0,1,1.071,2.166Z"
                          fill="#0078d4"
                        ></path>
                        <rect
                          x="2.328"
                          y="7.049"
                          width="6.281"
                          height="3.408"
                          rx="0.283"
                          fill="#0078d4"
                        ></rect>
                        <rect
                          x="9.336"
                          y="7.049"
                          width="6.281"
                          height="3.408"
                          rx="0.283"
                          fill="#fff"
                        ></rect>
                        <rect
                          x="2.296"
                          y="11.128"
                          width="6.281"
                          height="3.408"
                          rx="0.283"
                          fill="#0078d4"
                        ></rect>
                        <rect
                          x="9.304"
                          y="11.128"
                          width="6.281"
                          height="3.408"
                          rx="0.283"
                          fill="#0078d4"
                        ></rect>
                      </g>
                    </g>
                  </svg>
                </span>
              </div>
              <h3 className="feature-title">UX/UI Design</h3>
              <p className="feature-desc">
                Design intuitive interfaces that empower human agents to adopt
                new technologies naturally and maximize their daily
                productivity.
              </p>
            </div>
          </div>

          <div className="nearshore-section">
            <div className="nearshore-header">
              <h2 className="section-title">We Deliver Nearshore Excellence</h2>
              <p className="section-subtitle">
                Premium talent, timezone alignment, cultural fit, and cost
                efficiency. We operate where it matters most for North American
                and European enterprises.
              </p>
            </div>

            <div className="nearshore-grid">
              <div className="nearshore-card">
                <div className="nearshore-icon">
                  <Globe size={24} />
                </div>
                <h3>Strategic Location</h3>
                <ul>
                  <li>European headquarters in Portugal</li>
                  <li>Same timezone as UK: 4-8 hours ahead of EST</li>
                  <li>
                    Overlap with both European and North American business hours
                  </li>
                  <li>Easy travel access to major hubs</li>
                </ul>
              </div>

              <div className="nearshore-card">
                <div className="nearshore-icon">
                  <TrendingDown size={24} />
                </div>
                <h3>Cost Efficiency</h3>
                <ul>
                  <li>30–40% cost savings vs. onshore teams</li>
                  <li>Premium quality at nearshore pricing</li>
                  <li>No compromise on expertise or delivery</li>
                  <li>Predictable project costs in EUR/USD</li>
                </ul>
              </div>

              <div className="nearshore-card">
                <div className="nearshore-icon">
                  <Award size={24} />
                </div>
                <h3>Top-Tier Talent</h3>
                <ul>
                  <li>Microsoft Gold Partner</li>
                  <li>Microsoft certified specialists</li>
                  <li>English-speaking teams</li>
                  <li>Strong European tech education background</li>
                </ul>
              </div>

              <div className="nearshore-card">
                <div className="nearshore-icon">
                  <Handshake size={24} />
                </div>
                <h3>Cultural Alignment</h3>
                <ul>
                  <li>Western business protocols and work culture</li>
                  <li>Strong communication and collaboration</li>
                  <li>Agile and DevOps methodologies</li>
                  <li>Transparent project management</li>
                </ul>
              </div>

              <div className="nearshore-card">
                <div className="nearshore-icon">
                  <MessageSquare size={24} />
                </div>
                <h3>Real-Time Collaboration</h3>
                <ul>
                  <li>Live meetings during business hours</li>
                  <li>Instant communication channels</li>
                  <li>Quick turnaround times</li>
                  <li>24-hour response guarantee</li>
                </ul>
              </div>

              <div className="nearshore-card">
                <div className="nearshore-icon">
                  <Shield size={24} />
                </div>
                <h3>Data Protection</h3>
                <ul>
                  <li>GDPR compliant by default</li>
                  <li>EU data protection standards</li>
                  <li>ISO certifications and security protocols</li>
                  <li>Safe for sensitive enterprise data</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="team-extension-header">
            <h2 className="section-title">
              Team Extension: Scale Your Capabilities
            </h2>
            <p className="section-subtitle">
              Access specialized talent on-demand. From junior developers to
              solution architects, we have the expertise you need when you need
              it.
            </p>
          </div>

          <div className="team-extension-grid">
            <div className="team-extension-card">
              <h3
                style={{
                  color: "#2563eb",
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: "1rem",
                }}
              >
                Microsoft Ecosystem
              </h3>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.9rem",
                  marginBottom: "1.5rem",
                }}
              >
                Dynamics 365, Power Platform, Azure specialists ready to join
                your team.
              </p>
              <div
                style={{
                  display: "grid",
                  gap: "0.75rem",
                  fontSize: "0.9rem",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Solution Architect
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    10+ years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Senior Developer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    5-10 years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Mid-Level Developer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    3-5 years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Junior Developer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    1-3 years
                  </span>
                </div>
              </div>
              <div
                style={{
                  marginTop: "1.5rem",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                {[
                  "Dynamics 365",
                  "Power Apps",
                  "Power Automate",
                  "Power BI",
                  "Copilot Studio",
                  "Azure",
                  ".NET",
                  "C#",
                ].map((tech) => (
                  <span
                    key={tech}
                    style={{
                      display: "inline-block",
                      background: "#2563eb",
                      color: "white",
                      padding: "0.4rem 0.8rem",
                      borderRadius: "1rem",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="team-extension-card">
              <h3
                style={{
                  color: "#2563eb",
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: "1rem",
                }}
              >
                Full Stack Development
              </h3>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.9rem",
                  marginBottom: "1.5rem",
                }}
              >
                Modern web development expertise for custom applications and
                integrations.
              </p>
              <div
                style={{
                  display: "grid",
                  gap: "0.75rem",
                  fontSize: "0.9rem",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Tech Lead
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    8+ years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Senior Developer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    5-8 years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Mid-Level Developer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    3-5 years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Junior Developer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    1-3 years
                  </span>
                </div>
              </div>
              <div
                style={{
                  marginTop: "1.5rem",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                {[
                  "Vue.js",
                  "Angular",
                  "React",
                  "Node.js",
                  "API",
                  "Python",
                  "SQL",
                  "MongoDB",
                ].map((tech) => (
                  <span
                    key={tech}
                    style={{
                      display: "inline-block",
                      background: "#2563eb",
                      color: "white",
                      padding: "0.4rem 0.8rem",
                      borderRadius: "1rem",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="team-extension-card">
              <h3
                style={{
                  color: "#2563eb",
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginBottom: "1rem",
                }}
              >
                Specialized Services
              </h3>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.9rem",
                  marginBottom: "1.5rem",
                }}
              >
                QA, UX/UI data specialists, and other experts to complement your
                core team.
              </p>
              <div
                style={{
                  display: "grid",
                  gap: "0.75rem",
                  fontSize: "0.9rem",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    QA Lead
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    5+ years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    UX/UI Designer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    4+ years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    paddingBottom: "0.75rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Data Engineer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    4+ years
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    DevOps Engineer
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    4+ years
                  </span>
                </div>
              </div>
              <div
                style={{
                  marginTop: "1.5rem",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                {[
                  "Test Automation",
                  "Figma",
                  "Azure DevOps",
                  "ETL",
                  "Data Migration",
                  "CI/CD",
                ].map((tech) => (
                  <span
                    key={tech}
                    style={{
                      display: "inline-block",
                      background: "#2563eb",
                      color: "white",
                      padding: "0.4rem 0.8rem",
                      borderRadius: "1rem",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="contact-header">
            <h2 className="section-title">
              Get your FMS Agent opportunity assessment
            </h2>
            <p className="section-subtitle">Takes less than 30 seconds</p>
          </div>

          <div>
            <form className="card" onSubmit={handleSubmit}>
              {formStatus === "success" ? (
                <div style={{ textAlign: "center", padding: "2rem 0" }}>
                  <p
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                    }}
                  >
                    ✅ Thank you!
                  </p>
                  <p style={{ color: "var(--text-secondary)" }}>
                    We'll be in touch soon.
                  </p>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "1rem",
                    }}
                  >
                    <div className="form-group">
                      <label className="form-label">First name</label>
                      <input
                        type="text"
                        name="firstName"
                        className="form-input"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last name</label>
                      <input
                        type="text"
                        name="lastName"
                        className="form-input"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Work email *</label>
                    <input
                      type="email"
                      name="workEmail"
                      className="form-input"
                      placeholder="you@company.com"
                      value={formData.workEmail}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      name="company"
                      className="form-input"
                      placeholder="Company name"
                      value={formData.company}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Area of interest</label>
                    <select
                      name="areaOfInterest"
                      className="form-input"
                      value={formData.areaOfInterest}
                      onChange={handleInputChange}
                    >
                      <option value="">Select an area</option>
                      <option value="Dynamics 365">Dynamics 365</option>
                      <option value="Power Platform">Power Platform</option>
                      <option value="Copilot Studio">Copilot Studio</option>
                      <option value="Full Stack">Full Stack</option>
                      <option value="HubSpot">HubSpot</option>
                      <option value="Cloud & DevOps">Cloud & DevOps</option>
                      <option value="QA & Testing">QA & Testing</option>
                      <option value="UX/UI Design">UX/UI Design</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project description</label>
                    <textarea
                      name="projectDescription"
                      className="form-input"
                      placeholder="Tell us about your project..."
                      value={formData.projectDescription}
                      onChange={handleInputChange}
                      rows={3}
                      style={{ resize: "vertical" }}
                    />
                  </div>
                  {formStatus === "error" && (
                    <p
                      style={{
                        color: "#ef4444",
                        fontSize: "0.875rem",
                        margin: "0.5rem 0",
                      }}
                    >
                      {formError}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ marginTop: "1rem" }}
                    disabled={formStatus === "loading"}
                  >
                    {formStatus === "loading"
                      ? "Submitting..."
                      : "Request a Discovery Session"}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        {showFab && (
          <button
            className="fab-button attention-glow"
            onClick={() => navigate("/explore")}
            aria-label="Try FMS Agent Explorer"
          >
            <Bot size={24} />
          </button>
        )}

        <footer className="landing-footer">
          <div className="footer-content">
            <div className="footer-left">
              <span className="footer-brand">
                © {new Date().getFullYear()} Findmore Solutions. All rights
                reserved.
              </span>
            </div>
            <div className="footer-links">
              <a
                href="https://findmore.solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link"
              >
                Findmore Solutions Website
              </a>
              <span className="footer-divider">|</span>
              <Link to="/privacy" className="footer-link">
                Privacy Policy
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
